import {
  type CellData,
  type Column_Internal,
  type RowData,
  type TableFeature,
  type TableFeatures,
  type Table_Internal,
  type Updater,
  assignPrototypeAPIs,
  assignTableAPIs,
  functionalUpdate,
} from '@tanstack/react-table';

import type {
  ColumnLineWrapFeatureConstructors,
  DataTableLineWrapState,
} from './line-wrap-types.js';

/**
 * Global line wrap action for the DataTable to be used in the DataTable.Toolbar
 *
 * @public
 */
export function DataTableGlobalLineWrapAction() {
  return null;
}

/**
 * Determines which columns need to have their lineWrap state updated.
 * @param column - The column for which lineWrap was toggled.
 * @param value - The new lineWrap value to set. If not provided, the current value will be toggled.
 * @returns a record of column IDs and their new lineWrap states.
 */
function computeLineWrapStateUpdate<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  column: Column_Internal<TFeatures, TData, TValue>,
  value?: boolean,
): Record<string, boolean> {
  const newValue = value ?? !column.getIsLineWrapped();
  const childColumns = column.columns;

  if (childColumns.length === 0) {
    // For leaf or standalone columns, we only need to update the toggled column
    return {
      [column.id]: newValue,
    };
  }
  // If the toggled column is a parent column, we need to toggle all its child columns
  return Object.fromEntries(childColumns.map((child) => [child.id, newValue]));
}

/**
 * Helper function to toggle line wrap for a specific column, taking into account the current state and the column's capabilities.
 * @internal
 */
export function column_toggleLineWrap<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>, value: boolean): void {
  if (!column.getCanLineWrap()) {
    return;
  }

  column.table.setColumnLineWrap((oldValue) => {
    const columnsStatesToUpdate = computeLineWrapStateUpdate(column, value);

    // Check if the lineWrap was set as a global first
    if (typeof oldValue === 'boolean') {
      const columnIds = column.table
        .getAllLeafColumnsWithoutBuiltin()
        .map((col) => col.id);

      // when switching from a boolean, we need to preserve the current state of the other columns
      const currentColumnsState: Record<string, boolean> = Object.fromEntries(
        columnIds.map((id) => [id, oldValue]),
      );

      // When toggling a parent column, we need to set the state of all child columns
      return {
        ...currentColumnsState,
        ...columnsStatesToUpdate,
      };
    }

    return {
      ...oldValue,
      ...columnsStatesToUpdate,
    };
  });
}

/**
 * Helper function to determine if a specific column is currently line wrapped, taking into account global settings and parent-child relationships.
 * @internal
 */
export function column_getIsLineWrapped<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>): boolean {
  const lineWrapState = column.table.atoms.columnLineWrap?.get();
  // Boolean values define a global line wrapping on the table.
  if (typeof lineWrapState === 'boolean') {
    return lineWrapState;
  }

  const childColumns = column.columns;
  if (childColumns.length > 0) {
    // If the current column is a parent, we consider it line wrapped only if all child columns are line wrapped
    return childColumns.every((child) => child.getIsLineWrapped());
  }
  return lineWrapState?.[column.id] ?? false;
}

/**
 * Helper function to determine if a specific column can be line wrapped, based on the table's global settings.
 * @internal
 */
export function column_getCanLineWrap<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>) {
  return column.table.options.enableLineWrap ?? true;
}

/**
 * Helper function to update the global line wrap state on the table, which can be called from column-level actions to ensure consistent state management.
 * @internal
 */
export function table_setColumnLineWrap<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  updater: Updater<DataTableLineWrapState>,
): void {
  const newValue = functionalUpdate(
    updater,
    table.atoms.columnLineWrap?.get() ?? false,
  );
  table.options.onColumnLineWrapChange?.(newValue);
}

/**
 * Constructs the column line wrap feature for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructColumnLineWrapFeature(): TableFeature<ColumnLineWrapFeatureConstructors> {
  return {
    getInitialState: (initialState) => {
      return {
        columnLineWrap: null,
        ...initialState,
      };
      // This is safe here as the DataTableFeatures configuration, so it is a valid part of
      // TableState<TFeatures> at runtime — TypeScript just can't prove it
      // through the UnionToIntersection generic constraint.
    },
    assignColumnPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableColumnLineWrapFeature', prototype, table, {
        column_toggleLineWrap: {
          fn: (column, value: boolean) => column_toggleLineWrap(column, value),
        },
        column_getIsLineWrapped: {
          fn: (column) => column_getIsLineWrapped(column),
        },
        column_getCanLineWrap: {
          fn: (column) => column_getCanLineWrap(column),
        },
      });
    },
    constructTableAPIs: (table) => {
      assignTableAPIs('DataTableColumnLineWrapFeature', table, {
        table_setColumnLineWrap: {
          fn: (updater: Updater<DataTableLineWrapState>) =>
            table_setColumnLineWrap(table, updater),
        },
      });
    },
  };
}

/**
 * Feature implementation for LineWrap. This contains the logic that will be given
 * to the tanstack table. The implementing interface is important as these are
 * the function that will be called by the tanstack table core during render.
 * @internal
 */
export const DataTableColumnLineWrapFeature = constructColumnLineWrapFeature();
